import { Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { cn, formatCurrency } from '@/lib/utils'

ChartJS.register(ArcElement, Tooltip, Legend)

interface FeeItem {
  category: string
  amount: number
  color: string
}

interface FeeBreakdownProps {
  items: FeeItem[]
  title?: string
  className?: string
}

export function FeeBreakdown({
  items,
  title = 'Fee Breakdown',
  className,
}: FeeBreakdownProps) {
  const total = items.reduce((sum, item) => sum + item.amount, 0)

  const chartData = {
    labels: items.map((i) => i.category),
    datasets: [
      {
        data: items.map((i) => i.amount),
        backgroundColor: items.map((i) => i.color),
        borderWidth: 0,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: { label: string; parsed: number }) => {
            const percentage = ((context.parsed / total) * 100).toFixed(1)
            return `${context.label}: ${formatCurrency(context.parsed)} (${percentage}%)`
          },
        },
      },
    },
  }

  return (
    <div className={cn('bg-white rounded-lg border p-4', className)}>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 relative">
          <Doughnut data={chartData} options={options} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-sm font-bold">{formatCurrency(total)}</div>
            </div>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-600">{item.category}</span>
              </div>
              <span className="font-medium">{formatCurrency(item.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
